import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { Box, Button, type ButtonProps, Card, Stack, styled, TextField, Typography } from "~/common/mui";

import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { useTranslation } from "react-i18next";
import type { LineItem } from "~/features/cart/cart/models/CartData";
import { ContractItemParam, QuoteItemParam } from "~/features/cart/cart/components/LineItemStaticParams";
import { Price as PriceV3 } from "~/domains/pricing/components/Price/v3/Price";
import { PriceElements, type PriceProps } from "~/domains/pricing/components/Price/PriceV4";
import { Parameter } from "~/common/components/ParameterV2";
import { multiply } from "~/utils/PriceUtils";
import DateInput from "~/common/components/DateInput";
import { getEDDLabel } from "~/features/cart/cart/utils/cartUtils";
import { DateFormatType, formatDate } from "~/common/utils/DateFormatter";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import { canSeePriceRefColumn } from "~/features/quickEntry/utils/helpers";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { ProductParams2 } from "~/domains/product/components/ProductParamsV2";
import { useLineItemActions, useLineItemData } from "~/domains/cart/hooks";
import { LineItemMessagesSection } from "~/domains/cart/components";
import { SaleDiscountIndicator } from "~/domains/salesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";

import { useIsMinDesktopSizeM } from "~/common/hooks/useMediaQuery";

import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { LineItemStockIndicator } from "../cart/components/LineItemStockIndicator";
import { PartialDeliveriesSection } from "./PartialDeliveriesSection";
import { EditablePackagingCell } from "../cart/components/PackagingCell";
import { SaleQuantityIndicator } from "~/domains/salesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";
import Delete from "~/common/mui/icons/Delete";
import { ToggleCommentButton } from "./ToggleCommentButton";
import { IncludedSurchargeAsterisk, TariffSurchargeAsterisk } from "./commonComponents";
import { isLineLevelCustomerOrderNumberEnabled } from "~/utils/features";

interface LineItemComponentProps {
  className?: string;
  lineItem: LineItem;
  product: ProductDescriptor;
  onDelete?: () => void;
}

const DeleteButton = (props: ButtonProps) => {
  return <Button variant="text" color="textPrimary" startIcon={<Delete />} {...props} />;
};

const DetailsGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
  columnGap: "16px",
  rowGap: "16px",

  [theme.breakpoints.up("desktop-m")]: {
    gridTemplateColumns: "180px repeat(5, minmax(0px, 1fr))",
  },

  ["> .stock-indicator"]: {
    gridColumn: "1 / -1",
    justifySelf: "flex-start",

    [theme.breakpoints.up("desktop-m")]: {
      alignSelf: "flex-start",
      gridColumn: "1 / span 1",
      gridRow: 1,
    },
  },

  ["> .quantity"]: {
    gridColumn: "span 4",
    width: "168px",

    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "2 / span 1",
      gridRow: 1,
    },
  },

  ["> .packaging"]: {
    gridColumn: "span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "3 / span 1",
      gridRow: 1,
    },
  },

  ["> .delivery-date"]: {
    gridColumn: "span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "4 / span 1",
      gridRow: 1,
    },
  },

  ["> .net-price"]: {
    gridColumn: "span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "5 / span 1",
      gridRow: 1,
    },
  },

  ["> .total-price"]: {
    gridColumn: "span 4",
    justifySelf: "flex-start",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "6 / span 1",
      gridRow: 1,
    },
  },

  ["> .customer-order-number"]: {
    gridColumn: "1 / span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "2 / span 1",
      gridRow: 2,
    },
  },

  ["> .aliases"]: {
    gridColumn: "1 / span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "3 / span 1",
      gridRow: 2,
    },
  },

  ["> .contract-details, > .quote-details"]: {
    gridColumn: "9 / span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "4 / span 1",
      gridRow: 2,
    },
  },

  ["> .price-reference"]: {
    gridColumn: "span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "5 / span 1",
      gridRow: 2,
    },
  },

  ["> .gpsd"]: {
    gridColumn: "5 / span 4",
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "6 / span 1",
      gridRow: 2,
    },
  },

  ["> .messages"]: {
    gridColumn: "1 / -1",
    gridRow: 3,
    [theme.breakpoints.up("desktop-m")]: {
      gridColumn: "1 / span 4",
      gridRow: 3,
    },
  },
}));

export const LineItemComponent = ({ className, lineItem, product, onDelete }: LineItemComponentProps) => {
  const { t } = useTranslation();

  // Use shared hooks for data and actions
  const { pricing, sales, validation, flags, messages } = useLineItemData({ lineItem, product });
  const actions = useLineItemActions({ lineItem, product });

  // Destructure for convenience
  const { isOnSale, saleActive, salesPricing, salesStock } = sales;
  const { quantityVisible, isPMA, inStock } = flags;
  const { disableEditing } = validation;

  // Price values from hook or sales pricing
  const netPrice = pricing.netPrice;
  const tariffSurcharge = pricing.tariffSurcharge;
  const surchargeValue = pricing.surchargeValue;
  const price = saleActive && salesPricing ? salesPricing.price : netPrice;
  const oldPrice = saleActive && salesPricing ? salesPricing.oldPrice : undefined;
  const discountFormatted = saleActive && salesPricing ? salesPricing.discountFormatted : undefined;

  const totalPrice = netPrice ? multiply(netPrice, lineItem.quantity) : undefined;

  const showPriceReferenceSlot = canSeePriceRefColumn() || isOnSale;

  const aliases = ProductParams2.ProductAliases({
    mode: "15-digits",
    visibleAliasesLayout: "separate-rows",
    packCode: lineItem.packCode,
  })({
    t,
    product,
  });

  const gpsd = ProductParams2.GPSD({
    t,
    product,
  });

  const isMinDesktopM = useIsMinDesktopSizeM();

  const [commentRef, toggleComment] = useShowAndScrollInput(actions.toggleShowComment);

  return (
    <Card sx={{ p: 3 }} component={Stack} gap={3} className={className}>
      <DetailsGrid>
        <LineItemStockIndicator className="stock-indicator" product={product} packEntry={lineItem} />
        <Stack className="quantity" gap={1}>
          <QuantityInput
            variant="compact"
            value={lineItem.quantity}
            step={validation.minQuantityValue}
            onValueChange={actions.updateQty}
            error={validation.quantityHasError}
            helperLabel={validation.MOQHelperLabel}
            disabled={disableEditing}
            min={0}
            label={false}
          />
          {salesStock > 0 && saleActive && <SaleQuantityIndicator.V2 quantity={salesStock} />}
        </Stack>

        {netPrice && (
          <Parameter
            className="net-price"
            label={<PriceElements.NetPriceLabel variant="inherit" price={netPrice} />}
            value={
              <PriceElements.Stack gap={0}>
                <Stack direction="row" gap={1}>
                  <CurrentPrice
                    price={price}
                    zeroText={t("product.priceOnRequest")}
                    tariffSurcharge={tariffSurcharge}
                    variant="inherit"
                  />
                  {discountFormatted && (
                    <SaleDiscountIndicator.V2 variant="bodyMedium" discountFormatted={discountFormatted} />
                  )}
                </Stack>
                {oldPrice && <OldPrice price={oldPrice} variant="bodySmall" tariffSurcharge={tariffSurcharge} />}
                {!saleActive && (
                  <Typography variant="bodySmall">
                    <PriceV3.ExtraLabels price={netPrice} surchargeEnabled={false} />
                  </Typography>
                )}
              </PriceElements.Stack>
            }
          />
        )}

        {totalPrice && (
          <Parameter
            className="total-price"
            label={<PriceElements.TotalPriceLabel variant="inherit" price={totalPrice} />}
            value={
              <CurrentPrice price={totalPrice} variant="inherit" tariffSurcharge={tariffSurcharge} surchargeEnabled />
            }
          />
        )}

        {quantityVisible && !isPMA ? (
          <DateInput
            className="w-100 delivery-date"
            label={t("cartPage.table.header.delivery.title")}
            helperLabel={getEDDLabel(lineItem, t)}
            date={lineItem.manuallySetRDD ? lineItem.deliveryDate : null}
            placeholder={
              !lineItem.manuallySetRDD && lineItem.deliveryDate
                ? formatDate(lineItem.deliveryDate, DateFormatType.DATE)
                : undefined
            }
            onChange={actions.updateDate}
            onClear={actions.clearDate}
            showClear={lineItem.manuallySetRDD}
            isInvalid={!lineItem.deliveryDate}
            isDayDisabled={actions.isDayDisabled}
            disabled={disableEditing}
          />
        ) : (
          <div className="w-100 delivery-date">-</div>
        )}

        <Box className="packaging">
          <EditablePackagingCell product={product} lineItem={lineItem} onChange={actions.selectPackage} />
        </Box>

        {showPriceReferenceSlot && (
          <Stack className="price-reference">
            <PriceReferenceParam
              className="w-100 price-reference"
              product={product.base}
              priceReference={lineItem.priceReference}
              onChange={actions.updatePriceReference}
              data-test-id="package-entry-price-reference"
              disabled={disableEditing || saleActive}
              editable={!lineItem.quoteNumber && canSeePriceRefColumn()}
              size={isMinDesktopM ? "small" : "medium"}
            />
            <CheckboxWithLabel
              checked={saleActive}
              onCheck={actions.updateSalesEnabled}
              hidden={!isOnSale || !!lineItem.priceReference}
            >
              {t("feature.salesCampaign.specialDealPrice")}
            </CheckboxWithLabel>
          </Stack>
        )}

        {aliases && <Parameter className="aliases" {...aliases} />}

        {gpsd && <Parameter className="gpsd" {...gpsd} />}

        <QuoteItemParam className="quote-details" product={product.base} lineItem={lineItem} />
        <ContractItemParam className="contract-details" product={product.base} lineItem={lineItem} />

        {isLineLevelCustomerOrderNumberEnabled() && (
          <div className="customer-order-number">
            <TextField
              fullWidth
              label={t("search.type.customerOrderNumber")}
              inputProps={{ "data-test-id": "package-entry-po-number" }}
              value={lineItem.customerOrderNumber}
              onChange={actions.updatePONumber}
            />
          </div>
        )}

        <LineItemMessagesSection
          messages={messages}
          isPMA={isPMA}
          isOptime={flags.isOptime}
          quantityVisible={quantityVisible}
          className="messages"
        />
      </DetailsGrid>

      <OptionalBox>
        {surchargeValue && <IncludedSurchargeAsterisk value={surchargeValue} percent={lineItem.netPrice?.surcharge} />}
        {tariffSurcharge && <TariffSurchargeAsterisk />}
      </OptionalBox>

      <PartialDeliveriesSection partialDeliveries={lineItem.partialDeliveries} />

      <Stack direction={"row"} justifyContent={"space-between"}>
        <ToggleCommentButton open={lineItem.showComments} onClick={toggleComment} />
        <DeleteButton
          onClick={() => {
            actions.removeEntry();
            if (!inStock) {
              onDelete?.();
            }
          }}
          disabled={lineItem.isBundled && lineItem.original}
        >
          {t("cart.deleteLine")}
        </DeleteButton>
      </Stack>

      {lineItem.showComments && (
        <Box sx={{ width: { mobile: "100%", "desktop-m": "50%" } }}>
          <CommentInput
            value={lineItem.comment || ""}
            onChange={actions.updateComment}
            ref={commentRef}
            label={t("wishlistPage.comment.label")}
          />
        </Box>
      )}
    </Card>
  );
};

const OptionalBox = styled("div")({
  ["&:empty"]: {
    display: "none",
  },
});

const CurrentPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));
